import type {
  AuthUser,
  DashboardSummary,
  Participant,
  ReferenceTest,
  Sample,
  SampleRole,
  Shipment,
  WithdrawalScope,
} from '../types';

const API_BASE_URL = (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '');

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

const apiFetch = (url: string, options: RequestInit = {}) =>
  fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers ?? {}) },
    ...options,
  });

async function handleResponse<T>(response: Response): Promise<T> {
  const isJson = response.headers.get('content-type')?.includes('application/json');
  const body = isJson ? await response.json().catch(() => null) : null;

  if (!response.ok) {
    const message = body?.message || body?.error || `Forespørsel feilet (${response.status})`;
    throw new ApiError(message, response.status, body?.code || body?.error);
  }

  return (body?.data ?? body) as T;
}

// --- Auth --------------------------------------------------------------

export const login = (email: string, password: string): Promise<{ user: AuthUser }> =>
  apiFetch(`${API_BASE_URL}/auth/login`, { method: 'POST', body: JSON.stringify({ email, password }) }).then((r) =>
    handleResponse(r),
  );

export const logout = (): Promise<void> =>
  apiFetch(`${API_BASE_URL}/auth/logout`, { method: 'POST' }).then(() => undefined);

export const getSession = (): Promise<{ authenticated: boolean; user?: AuthUser }> =>
  apiFetch(`${API_BASE_URL}/auth/session`).then((r) => handleResponse(r));

export const validateInvitation = (
  token: string,
): Promise<{ email: string; name: string; role: string; expiresAt: string }> =>
  apiFetch(`${API_BASE_URL}/auth/invitations/${encodeURIComponent(token)}`).then((r) => handleResponse(r));

export const acceptInvitation = (token: string, name: string, password: string): Promise<{ user: AuthUser }> =>
  apiFetch(`${API_BASE_URL}/auth/invitations/${encodeURIComponent(token)}/accept`, {
    method: 'POST',
    body: JSON.stringify({ name, password }),
  }).then((r) => handleResponse(r));

export const requestPasswordReset = (email: string): Promise<{ message: string }> =>
  apiFetch(`${API_BASE_URL}/auth/password-reset/request`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  }).then((r) => handleResponse(r));

export const confirmPasswordReset = (token: string, password: string): Promise<{ message: string }> =>
  apiFetch(`${API_BASE_URL}/auth/password-reset/confirm`, {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  }).then((r) => handleResponse(r));

// --- Dashboard -----------------------------------------------------------

export const getDashboardSummary = (): Promise<DashboardSummary> =>
  apiFetch(`${API_BASE_URL}/dashboard`).then((r) => handleResponse(r));

// --- Participants ----------------------------------------------------------

export const getParticipants = (): Promise<Participant[]> =>
  apiFetch(`${API_BASE_URL}/participants`).then((r) => handleResponse(r));

export const getParticipant = (id: string): Promise<Participant> =>
  apiFetch(`${API_BASE_URL}/participants/${id}`).then((r) => handleResponse(r));

export interface CreateParticipantInput {
  pseudonymId: string;
  ageYears?: number | null;
  sex?: string | null;
  inclusionDate?: string | null;
}

export const createParticipant = (data: CreateParticipantInput): Promise<Participant> =>
  apiFetch(`${API_BASE_URL}/participants`, { method: 'POST', body: JSON.stringify(data) }).then((r) =>
    handleResponse(r),
  );

export const updateParticipant = (id: string, data: Partial<Participant>): Promise<Participant> =>
  apiFetch(`${API_BASE_URL}/participants/${id}`, { method: 'PATCH', body: JSON.stringify(data) }).then((r) =>
    handleResponse(r),
  );

// --- Consents ----------------------------------------------------------

export interface CreateConsentInput {
  participantId: string;
  consentVersion: string;
  signedDate: string;
  commercialModelConsent: boolean;
}

export const createConsent = (data: CreateConsentInput) =>
  apiFetch(`${API_BASE_URL}/consents`, { method: 'POST', body: JSON.stringify(data) }).then((r) => handleResponse(r));

// ADMIN-only server-side (H4) — withdrawal is a higher-impact action than
// routine registration.
export const withdrawConsent = (
  id: string,
  withdrawalDate: string,
  withdrawalScope: WithdrawalScope,
  withdrawalNote?: string,
) =>
  apiFetch(`${API_BASE_URL}/consents/${id}/withdraw`, {
    method: 'POST',
    body: JSON.stringify({ withdrawalDate, withdrawalScope, withdrawalNote }),
  }).then((r) => handleResponse(r));

// --- Samples ----------------------------------------------------------

export interface CreateSampleInput {
  participantId: string;
  type: string;
  takenAt?: string;
  takenByRole: SampleRole;
  cryoLabelId: string;
  freezerBox?: string;
  freezerRow?: string;
  freezerColumn?: string;
  doubleCheckConfirmed: boolean;
  doubleCheckByRole?: SampleRole;
}

export const getSamples = (status?: string): Promise<Sample[]> =>
  apiFetch(`${API_BASE_URL}/samples${status ? `?status=${status}` : ''}`).then((r) => handleResponse(r));

export const createSample = (data: CreateSampleInput): Promise<Sample> =>
  apiFetch(`${API_BASE_URL}/samples`, { method: 'POST', body: JSON.stringify(data) }).then((r) => handleResponse(r));

export const updateSample = (id: string, data: Partial<Sample>): Promise<Sample> =>
  apiFetch(`${API_BASE_URL}/samples/${id}`, { method: 'PATCH', body: JSON.stringify(data) }).then((r) =>
    handleResponse(r),
  );

// --- Shipments ----------------------------------------------------------

export const getShipments = (): Promise<Shipment[]> =>
  apiFetch(`${API_BASE_URL}/shipments`).then((r) => handleResponse(r));

export interface CreateShipmentInput {
  destinationLab: string;
  sampleIds: string[];
}

export const createShipment = (data: CreateShipmentInput): Promise<Shipment> =>
  apiFetch(`${API_BASE_URL}/shipments`, { method: 'POST', body: JSON.stringify(data) }).then((r) =>
    handleResponse(r),
  );

export const updateShipment = (id: string, data: Partial<Shipment>): Promise<Shipment> =>
  apiFetch(`${API_BASE_URL}/shipments/${id}`, { method: 'PATCH', body: JSON.stringify(data) }).then((r) =>
    handleResponse(r),
  );

// --- Reference tests -------------------------------------------------------

export interface CreateReferenceTestInput {
  participantId: string;
  vendor: string;
  orderedDate?: string;
}

export const createReferenceTest = (data: CreateReferenceTestInput): Promise<ReferenceTest> =>
  apiFetch(`${API_BASE_URL}/reference-tests`, { method: 'POST', body: JSON.stringify(data) }).then((r) =>
    handleResponse(r),
  );

export const updateReferenceTest = (id: string, data: Partial<ReferenceTest>): Promise<ReferenceTest> =>
  apiFetch(`${API_BASE_URL}/reference-tests/${id}`, { method: 'PATCH', body: JSON.stringify(data) }).then((r) =>
    handleResponse(r),
  );

// --- Export ----------------------------------------------------------

export const exportParticipantsCsvUrl = (): string => `${API_BASE_URL}/export/participants.csv`;
